import { listPublicPackagesOptions } from "@/actions/coaching-package/handler";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  ContactRound,
  Lightbulb,
  ShieldCheck,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_app/coachs")({
  head: () => ({
    title:
      "Koçluk Paketleri - Online Fitness ve Antrenman Programları | Alican Atalay",
    meta: [
      {
        name: "description",
        content:
          "Alican Atalay ile online koçluk, fitness ve antrenman programları. Başlangıç ve ileri seviye sporcular için kişiselleştirilmiş kuvvet, mobilite ve beslenme rehberi. WhatsApp destekli, video anlatımlı, hedef odaklı koçluk paketleri.",
      },
      {
        name: "keywords",
        content:
          "Alican Atalay, online koçluk, fitness programı, antrenman paketi, kuvvet programı, beslenme rehberi, sporcu koçu, WhatsApp destekli koçluk, video egzersiz rehberi, kişisel antrenör, platoları aşma, başlangıç fitness, online spor danışmanlığı, Atalay The Coach",
      },
    ],
  }),

  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listPublicPackagesOptions()),
  component: PricingPageComponent,
});

// PricingGrid component to render the cards
function PricingGrid({
  plans,
}: {
  plans: Array<{
    title: string;
    description: string;
    price: number;
    oldPrice?: number;
    priceDetails: string;
    features: string[];
    continuationPlan?: string;
    ctaText: string;
    ctaLink: string;
    isRecommended?: boolean;
    imageUrl?: string;
  }>;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2 xl:gap-10">
      {plans.map((plan) => (
        <Card
          key={plan.title}
          className={cn(
            "group relative flex flex-col overflow-hidden border-2 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl",
            plan.isRecommended
              ? "border-primary/20 bg-secondary/5"
              : "border-border hover:border-primary/30",
          )}
        >
          {/* Package Image */}
          {plan.imageUrl && (
            <div className="relative h-48 w-full overflow-hidden bg-muted/30">
              <img
                src={plan.imageUrl}
                alt={plan.title}
                className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              {/* Premium badge overlay for recommended packages */}
              {plan.isRecommended && (
                <div className="absolute top-4 right-4 z-10">
                  <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground text-xs shadow-lg">
                    ÖNERİLEN
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Premium badge for recommended packages (when no image) */}
          {!plan.imageUrl && plan.isRecommended && (
            <div className="absolute top-4 right-4 z-10">
              <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground text-xs">
                ÖNERİLEN
              </span>
            </div>
          )}

          <CardHeader className="pt-6 pb-6">
            <div className="space-y-2">
              <CardTitle className="font-bold text-xl leading-tight sm:text-2xl">
                {plan.title}
              </CardTitle>
              <CardDescription className="text-sm leading-relaxed sm:text-base">
                {plan.description}
              </CardDescription>
            </div>

            {/* Price section */}
            <div className="flex flex-col justify-start pt-4">
              <div className="flex items-baseline space-x-2">
                {plan.oldPrice && plan.oldPrice !== plan.price && (
                  <span className="font-medium text-lg text-muted-foreground line-through">
                    ₺{plan.oldPrice.toLocaleString()}
                  </span>
                )}
                <span className="font-extrabold text-3xl text-primary tracking-tight sm:text-4xl">
                  ₺{plan.price.toLocaleString()}
                </span>
              </div>
              <p className="mt-2 text-muted-foreground text-xs sm:text-left sm:text-sm">
                {plan.priceDetails}
              </p>
              <div className="flex justify-start">
                {plan.oldPrice && plan.oldPrice !== plan.price && (
                  <div className="mt-2 inline-flex items-center rounded-full bg-destructive/10 px-2 py-1">
                    <span className="font-medium text-destructive text-xs">
                      %
                      {Math.round(
                        ((plan.oldPrice - plan.price) / plan.oldPrice) * 100,
                      )}{" "}
                      İNDİRİM
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="grow px-6 pb-6">
            <div className="space-y-3">
              <h4 className="font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                {plan.isRecommended ? "Paket İçeriği" : "Hizmet İçeriği"}
              </h4>
              <ul className="space-y-3">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">
                      <Check className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground text-sm leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>

          <CardFooter className="border-t bg-accent/20 p-6">
            <div className="w-full space-y-4">
              {plan.continuationPlan && (
                <div className="rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 p-3">
                  <p className="text-center text-muted-foreground text-xs sm:text-sm">
                    <Lightbulb className="mr-1 inline h-4 w-4" />
                    {plan.continuationPlan}
                  </p>
                </div>
              )}

              <Button
                asChild
                size="lg"
                className="w-full bg-primary font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]"
              >
                <a
                  href={plan.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  {plan.ctaText}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
            </div>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function PricingPageComponent() {
  const { data } = useSuspenseQuery(listPublicPackagesOptions());

  const coachingPackages = data.body?.coaching || [];
  const consultingServices = data.body?.consulting || [];
  return (
    <div className="min-h-screen w-full bg-background px-4 pt-28 pb-20 text-foreground antialiased sm:px-8">
      <div className="mx-auto w-full max-w-7xl">
        {/* Hero Section */}
        <div className="mb-16 text-center sm:mb-20">
          <div className="mx-auto max-w-3xl space-y-6">
            <h1 className="font-extrabold text-4xl text-foreground tracking-tight sm:text-5xl lg:text-6xl">
              Koçluk ve Ürünler
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground leading-relaxed sm:text-xl">
              Fitness hedeflerinize ulaşmak için profesyonel destek alın.
              Kişiselleştirilmiş programlar ve uzman rehberliği ile başarıya
              adım atın.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-4 text-muted-foreground text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Kişiye Özel Programlar</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Uzman Rehberlik</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>WhatsApp Desteği</span>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="coaching" className="w-full">
          <TabsList className="mx-auto mb-12 grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="coaching" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Koçluk</span>
              <span className="sm:hidden">Koçluk</span>
            </TabsTrigger>
            <TabsTrigger value="consulting" className="flex items-center gap-2">
              <ContactRound className="h-4 w-4" />
              <span className="hidden sm:inline">Danışmanlık</span>
              <span className="sm:hidden">Danışmanlık</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coaching" className="mt-0">
            <div className="mb-8 text-center">
              <h2 className="font-bold text-2xl text-foreground sm:text-3xl">
                Koçluk Paketleri
              </h2>
              <p className="mt-2 text-muted-foreground">
                Uzun vadeli hedefleriniz için kapsamlı destek programları
              </p>
            </div>
            <PricingGrid plans={coachingPackages} />
          </TabsContent>

          <TabsContent value="consulting" className="mt-0">
            <div className="mb-8 text-center">
              <h2 className="font-bold text-2xl text-foreground sm:text-3xl">
                Danışmanlık Hizmetleri
              </h2>
              <p className="mt-2 text-muted-foreground">
                Tek seferlik uzman görüşleri ve teknik analizler
              </p>
            </div>
            <PricingGrid plans={consultingServices} />
          </TabsContent>
        </Tabs>

        {/* Sade Güvenli Ödeme & Sözleşmeler Bandı */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl border border-border/60 bg-muted/20 px-5 py-4 text-center sm:flex-row sm:text-left">
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-1.5 font-medium text-foreground text-xs sm:justify-start">
              <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
              <span>İyzico 256-Bit SSL ile 3D Secure Güvenli Ödeme</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-muted-foreground text-xs sm:justify-start">
              <Link
                to="/privacy"
                search={{ tab: "distance-sales" }}
                className="text-primary hover:underline"
              >
                Mesafeli Satış Sözleşmesi
              </Link>
              <span>•</span>
              <Link
                to="/privacy"
                search={{ tab: "delivery-return" }}
                className="text-primary hover:underline"
              >
                Teslimat & İade Politikası
              </Link>
            </div>
          </div>

          <div className="flex items-center justify-center rounded-lg border border-border/60 bg-white/95 px-3 py-1.5 dark:bg-card/80">
            <img
              src="/iyzico-logo-pack/footer_iyzico_ile_ode/Colored/logo_band_colored.svg"
              alt="İyzico ile Öde, Visa, Mastercard, Troy, Amex"
              className="h-5 w-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
